// 오디오 업로드 및 관리 서비스

class AudioService {
  constructor() {
    this.baseURL = process.env.NODE_ENV === 'production' 
      ? 'https://kmhak.com/api' 
      : '/api';
  }

  // 오디오 파일 업로드 (레거시 메서드 - 이제 내부적으로 분할 업로드 사용)
  async uploadAudio(audioBlob, metadata = {}) {
    console.log(`업로드 시작 - 크기: ${audioBlob.size} bytes`);
    // 이제 모든 업로드는 분할 업로드 방식으로 처리
    return this.uploadAudioChunked(audioBlob, metadata);
  }

  // 분할 업로드 (모든 파일에 사용)
  async uploadAudioChunked(audioBlob, metadata = {}, onProgress = null) {
    const chunkSize = 3 * 1024 * 1024; // 3MB chunks
    const totalChunks = Math.ceil(audioBlob.size / chunkSize);
    const uploadId = this.generateUploadId();

    try {
      console.log(`분할 업로드 시작 - 총 ${totalChunks}개 청크`);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, audioBlob.size);
        const chunk = audioBlob.slice(start, end);

        await this.uploadChunk(chunk, uploadId, i, totalChunks, metadata);

        if (onProgress) {
          onProgress({
            loaded: end,
            total: audioBlob.size,
            percentage: Math.round((end / audioBlob.size) * 100)
          });
        }
      }

      // 업로드 완료 신호
      return await this.completeChunkedUpload(uploadId, metadata, totalChunks);

    } catch (error) {
      console.error('분할 업로드 오류:', error);
      throw error;
    }
  }

  // 청크 업로드
  async uploadChunk(chunk, uploadId, chunkIndex, totalChunks, metadata) {
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('uploadId', uploadId);
    formData.append('chunkIndex', chunkIndex);
    formData.append('totalChunks', totalChunks);
    
    // 첫 번째 청크에만 메타데이터 포함
    if (chunkIndex === 0) {
      console.log('첫 번째 청크에 메타데이터 포함:', metadata);
      formData.append('metadata', JSON.stringify(metadata));
    }

    const response = await fetch(`${this.baseURL}/upload-audio-chunk`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': await this.getAuthHeader()
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`청크 ${chunkIndex} 업로드 실패:`, errorText);
      throw new Error(`청크 업로드 실패: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result;
  }

  // 분할 업로드 완료
  async completeChunkedUpload(uploadId, metadata, totalChunks) {
    const response = await fetch(`${this.baseURL}/complete-audio-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': await this.getAuthHeader()
      },
      body: JSON.stringify({
        uploadId,
        metadata,
        totalChunks
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`업로드 완료 실패: ${response.status} - ${errorText}`);
    }

    return await response.json();
  }

  // 업로드된 오디오 목록 조회
  async getAudioList(page = 1, limit = 20) {
    try {
      const response = await fetch(
        `${this.baseURL}/audio-list?page=${page}&limit=${limit}`,
        {
          headers: {
            'Authorization': await this.getAuthHeader()
          }
        }
      );

      if (!response.ok) {
        throw new Error(`목록 조회 실패: ${response.status}`);
      }

      return await response.json();

    } catch (error) {
      console.error('오디오 목록 조회 오류:', error);
      throw error;
    }
  }

  // 오디오 파일 삭제
  async deleteAudio(audioId) {
    try {
      const response = await fetch(`${this.baseURL}/audio/${audioId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': await this.getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error(`삭제 실패: ${response.status}`);
      }

      return await response.json();

    } catch (error) {
      console.error('오디오 삭제 오류:', error);
      throw error;
    }
  }

  // 오디오 파일 다운로드 URL 생성
  async getDownloadUrl(audioId) {
    try {
      const response = await fetch(`${this.baseURL}/audio/${audioId}/download-url`, {
        headers: {
          'Authorization': await this.getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error(`다운로드 URL 생성 실패: ${response.status}`);
      }

      const result = await response.json();
      return result.downloadUrl;

    } catch (error) {
      console.error('다운로드 URL 생성 오류:', error);
      throw error;
    }
  }

  // 인증 헤더 생성
  async getAuthHeader() {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      return `Bearer ${token}`;
    } catch (error) {
      console.error('인증 토큰 가져오기 실패:', error);
      return '';
    }
  }

  // 업로드 ID 생성
  generateUploadId() {
    return 'upload_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // 파일 크기 포맷팅
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // 오디오 지속시간 계산
  async getAudioDuration(audioBlob) {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.onloadedmetadata = () => {
        resolve(Math.round(audio.duration));
      };
      audio.onerror = () => {
        resolve(0);
      };
      audio.src = URL.createObjectURL(audioBlob);
    });
  }

  // 브라우저 지원 확인
  static isSupported() {
    return !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.MediaRecorder &&
      window.indexedDB
    );
  }

  // Safari 최적화 설정
  static getSafariOptimizedOptions() {
    return {
      mimeType: 'audio/mp4',
      audioBitsPerSecond: 128000
    };
  }

  // 전사 파일 조회
  async getTranscription(audioId) {
    try {
      const response = await fetch(`${this.baseURL}/audio/${audioId}/transcription`, {
        headers: {
          'Authorization': await this.getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error(`전사 파일 조회 실패: ${response.status}`);
      }

      return await response.json();

    } catch (error) {
      console.error('전사 파일 조회 오류:', error);
      throw error;
    }
  }

  // 요약 문서 조회
  async getSummary(audioId) {
    try {
      const response = await fetch(`${this.baseURL}/audio/${audioId}/summary`, {
        headers: {
          'Authorization': await this.getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error(`요약 문서 조회 실패: ${response.status}`);
      }

      return await response.json();

    } catch (error) {
      console.error('요약 문서 조회 오류:', error);
      throw error;
    }
  }

  // 마이크 권한 확인
  static async checkMicrophonePermission() {
    try {
      const result = await navigator.permissions.query({ name: 'microphone' });
      return result.state; // 'granted', 'denied', 'prompt'
    } catch (error) {
      console.warn('권한 확인 불가:', error);
      return 'unknown';
    }
  }
}

export default AudioService;